export const ProgramTypes = {
  GET_PROGRAM_ONBOARD_REQUEST: "GET_PROGRAM_ONBOARD_REQUEST",
  GET_PROGRAM_ONBOARD_SUCCESS: "GET_PROGRAM_ONBOARD_SUCCESS",
  GET_PROGRAM_ONBOARD_FAILURE: "GET_PROGRAM_ONBOARD_FAILURE",
  GET_PROGRAM_METRICS_REQUEST: "GET_PROGRAM_METRICS_REQUEST",
  GET_PROGRAM_METRICS_SUCCESS: "GET_PROGRAM_METRICS_SUCCESS",
  GET_PROGRAM_METRICS_FAILURE: "GET_PROGRAM_METRICS_FAILURE",
  GET_PROGRAM_DETAIL_REQUEST: "GET_PROGRAM_DETAIL_REQUEST",
  GET_PROGRAM_DETAIL_SUCCESS: "GET_PROGRAM_DETAIL_SUCCESS",
  GET_PROGRAM_DETAIL_FAILURE: "GET_PROGRAM_DETAIL_FAILURE",
};

export interface ProgramOnboardItem {
  program_name: string;
  cm: string;
  cm_location: string;
  operation: string;
  eom: string | null;
  program_mapping: string[];
}

export interface ProgramOnboardData {
  programs: ProgramOnboardItem[];
}

export interface ProgramOnboardResponse {
  status: string;
  data: ProgramOnboardData;
}

export interface ProgramMetricsRequest {
  programs: string[];
  cm: string[];
  location: string[];
  operation: string[];
  eom: string[];
  time_granularity: string;
  time_value: string;
}

export interface ProgramMetricsOtdcItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  hit_rate: number;
  prev_hit_rate: number;
  status: string;
  requested_shipment_qty: number;
  actual_shipment_qty: number;
  gap_shipment: number;
  quantity_of_shipped: number;
}

export interface ProgramMetricsStageData {
  hit_rate?: number | null;
  prev_hit_rate?: number | null;
  status?: string;
  machine_downtime?: number | null;
  prev_machine_downtime?: number | null;
  fpy?: number | null;
  cum_fpy?: number | null;
  prev_fpy?: number | null;
}

export interface ProgramMetricsOutputHitRateItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stages: Record<string, { hit_rate: number; prev_hit_rate: number; status: string; actual_output?: number; plan_output?: number }>;
}

export interface ProgramMetricsMachineDowntimeItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stages: Record<string, { machine_downtime: number; prev_machine_downtime: number; status: string }>;
}

export interface ProgramMetricsLineUtilItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  line_utilization: number;
  prev_line_utilization: number;
  status: string;
  cum_utilization: number;
}

export interface ProgramMetricsFpyItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stages: Record<string, { fpy: number | null; cum_fpy: number | null; prev_fpy: number | null; status: string }>;
}

export interface ProgramMetricsPhicCauseType {
  cause_type: string;
  qty: number;
  closed: number;
  open: number;
  acknowledged: number;
}

export interface ProgramMetricsData {
  otdc: {
    programs: ProgramMetricsOtdcItem[];
    overall_status: string;
  };
  output_hit_rate: {
    programs: ProgramMetricsOutputHitRateItem[];
    overall_status: string;
  };
  machine_downtime: {
    programs: ProgramMetricsMachineDowntimeItem[];
    overall_status: string;
  };
  line_utilization: {
    programs: ProgramMetricsLineUtilItem[];
    overall_status: string;
  };
  fpy: {
    programs: ProgramMetricsFpyItem[];
    overall_status: string;
  };
  phic: {
    cause_type_summary: ProgramMetricsPhicCauseType[];
  };
}

export interface ProgramMetricsResponse {
  status: string;
  data: ProgramMetricsData;
}

export interface ProgramDetailRequest {
  card_type: string;
  programs: string[];
  cm: string[];
  location: string[];
  operation: string[];
  eom: string[];
  time_granularity: string;
  time_value: string;
}

// OTD-C detail table row
export interface DetailOtdcRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  requested_shipment_qty: number;
  actual_shipment_qty: number;
  gap_shipment: number;
  hit_rate: number;
  quantity_of_shipped: number;
}

// Output Hit Rate detail table row
export interface DetailOutputHitRateRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  type: string;
  plan: number | null;
  actual: number | null;
  gap: number | null;
  cum: number | null;
  hit_rate: number | null;
}

// Machine Downtime detail table row
export interface DetailMachineDowntimeRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  machine_downtime: number;
  target: number;
}

// Machine Downtime trend data point
export interface DetailMachineDowntimeTrendDataPoint {
  period: string;
  machine_downtime: number | null;
}

// Machine Downtime trend item (one per program/cm/stage combination)
export interface DetailMachineDowntimeTrend {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  target: number;
  data: DetailMachineDowntimeTrendDataPoint[];
}

// Line Utilization detail table row
export interface DetailLineUtilizationRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  week: string;
  first_day: string;
  uph: number;
  eff_max: number;
  target: number;
  lines_approved: number;
  working_hours_per_shift: number;
  shift: number;
  max_drr: number;
  max_wrr7: number;
  weekly_cap: number;
  max_wrr?: number;
  weekly_output?: number;
  line_utilization: number;
  cum_cap: number;
  cum_output: number;
  cum_utilization: number;
}

// Line Utilization trend data point
export interface DetailLineUtilizationTrendDataPoint {
  period: string;
  line_utilization: number | null;
}

// Line Utilization trend item (one per program/cm combination)
export interface DetailLineUtilizationTrend {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  target: number;
  data: DetailLineUtilizationTrendDataPoint[];
}

// FPY detail table row
export interface DetailFpyRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  fpy: number;
  cum_fpy: number;
  rpy: number | null;
  cum_rpy: number | null;
  target: number;
}

// FPY trend data point
export interface DetailFpyTrendDataPoint {
  period: string;
  fpy: number | null;
  rpy?: number | null;
  actual_input?: number | null;
}

// FPY trend item (one per program/cm/stage combination)
export interface DetailFpyTrend {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  target: number;
  data: DetailFpyTrendDataPoint[];
}

// PHIC Alert Status detail table row
export interface DetailPhicAlertRow {
  date: string;
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  stage: string;
  alert_type: string;
  status: string;
  cause_type: string;
  alert_reason: string;
}

// PHIC trend series
export interface DetailPhicTrendSeries {
  cause_type: string;
  data: number[];
}

export interface DetailPhicTrend {
  dates: string[];
  series: DetailPhicTrendSeries[];
  totals: number[];
}

// Union detail data type
export interface ProgramDetailData {
  table: any[];
  trend?: any;
}

export type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export interface ProgramState {
  onboard: {
    data: ProgramOnboardData | null;
    status: RequestStatus;
    error: string | null;
  };
  metrics: {
    data: ProgramMetricsData | null;
    status: RequestStatus;
    error: string | null;
  };
  detail: {
    data: ProgramDetailData | null;
    cardType: string | null;
    status: RequestStatus;
    error: string | null;
  };
}
